import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

export type CameraPreset = 'overview' | 'turbine' | 'wave' | 'nacelle';

export class CameraController {
  public camera: THREE.PerspectiveCamera;
  public controls: OrbitControls;

  private isTransitioning: boolean = false;
  private targetPosition: THREE.Vector3 = new THREE.Vector3();
  private targetLookAt: THREE.Vector3 = new THREE.Vector3();
  private transitionAlpha: number = 0;
  private startPosition: THREE.Vector3 = new THREE.Vector3();
  private startLookAt: THREE.Vector3 = new THREE.Vector3();
  private transitionDuration: number = 1.2;
  private transitionTimer: number = 0;

  // Preset definitions
  public static readonly PRESETS: Record<CameraPreset, { pos: [number, number, number]; lookAt: [number, number, number] }> = {
    overview: {
      pos: [95, 55, 130],
      lookAt: [0, 38, 0]
    },
    turbine: {
      pos: [25, 75, 60],
      lookAt: [0, 78, 0]
    },
    wave: {
      pos: [22, 6, 28],
      lookAt: [0, 2, 0]
    },
    nacelle: {
      pos: [7.5, 92.2, 5.5],
      lookAt: [0, 90.5, -1.5]
    }
  };

  constructor(camera: THREE.PerspectiveCamera, domElement: HTMLElement) {
    this.camera = camera;
    this.controls = new OrbitControls(this.camera, domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.05;
    this.controls.maxPolarAngle = Math.PI / 2 - 0.02; // Don't go below sea level
    this.controls.minDistance = 2;
    this.controls.maxDistance = 320;

    // Set initial position
    this.setPreset('overview', false);
  }

  public setPreset(preset: CameraPreset, animate: boolean = true) {
    const config = CameraController.PRESETS[preset];
    if (!config) return;

    const targetPos = new THREE.Vector3(...config.pos);
    const targetLook = new THREE.Vector3(...config.lookAt);

    if (!animate) {
      this.camera.position.copy(targetPos);
      this.controls.target.copy(targetLook);
      this.controls.update();
      this.isTransitioning = false;
    } else {
      this.startPosition.copy(this.camera.position);
      this.startLookAt.copy(this.controls.target);
      this.targetPosition.copy(targetPos);
      this.targetLookAt.copy(targetLook);
      this.transitionTimer = 0;
      this.isTransitioning = true;
      this.controls.enabled = false;
    }
  }

  public resetView(animate: boolean = true) {
    this.setPreset('overview', animate);
  }

  public update(delta: number) {
    if (this.isTransitioning) {
      this.transitionTimer += delta;
      const progress = Math.min(this.transitionTimer / this.transitionDuration, 1.0);
      
      // Smooth ease-in-out curve
      const ease = progress < 0.5 
        ? 2 * progress * progress 
        : -1 + (4 - 2 * progress) * progress;

      this.camera.position.lerpVectors(this.startPosition, this.targetPosition, ease);
      this.controls.target.lerpVectors(this.startLookAt, this.targetLookAt, ease);

      if (progress >= 1.0) {
        this.isTransitioning = false;
        this.controls.enabled = true;
      }
    }

    this.controls.update();
  }
}
